import { CartUpdateEvent } from "./events.js";
import { morphSection } from "./section-renderer.js";

class SbBundleQuantity extends HTMLElement {
  connectedCallback() {
    this._input = this.querySelector("input");
    this._input?.addEventListener("change", this._handleChange);

    this.querySelector("[name='minus']")?.addEventListener(
      "click",
      this._handleMinus
    );
    this.querySelector("[name='plus']")?.addEventListener(
      "click",
      this._handlePlus
    );
    this.querySelector(".cart-items__remove")?.addEventListener(
      "click",
      this._handleRemove
    );
  }

  disconnectedCallback() {
    clearTimeout(this._debounceTimer);
    this._input?.removeEventListener("change", this._handleChange);
    this.querySelector("[name='minus']")?.removeEventListener(
      "click",
      this._handleMinus
    );
    this.querySelector("[name='plus']")?.removeEventListener(
      "click",
      this._handlePlus
    );
    this.querySelector(".cart-items__remove")?.removeEventListener(
      "click",
      this._handleRemove
    );
  }

  /** @returns {string[]} */
  get _keys() {
    return (this.dataset.keys ?? "").split(",").filter(Boolean);
  }

  /**
   * Collects all cart-items-component section IDs on the page,
   * matching how component-cart-items.js builds sectionsToUpdate.
   * @returns {{ sectionId: string | undefined, sections: string }}
   */
  get _sectionInfo() {
    const nearest = this.closest("cart-items-component");
    const sectionId =
      nearest instanceof HTMLElement ? nearest.dataset.sectionId : undefined;

    const sectionsToUpdate = new Set(sectionId ? [sectionId] : []);
    for (const el of document.querySelectorAll("cart-items-component")) {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        sectionsToUpdate.add(el.dataset.sectionId);
      }
    }

    return { sectionId, sections: Array.from(sectionsToUpdate).join(",") };
  }

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  _debounceTimer = undefined;

  /** @param {number} qty */
  _scheduleUpdate(qty) {
    clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => this._updateAll(qty), 400);
  }

  _handleMinus = () => {
    if (!this._input) {
      return;
    }
    const next = Math.max(1, Number(this._input.value) - 1);
    this._input.value = String(next);
    this._scheduleUpdate(next);
  };

  _handlePlus = () => {
    if (!this._input) {
      return;
    }
    const next = Number(this._input.value) + 1;
    this._input.value = String(next);
    this._scheduleUpdate(next);
  };

  _handleChange = () => {
    const qty = Math.max(1, Number(this._input?.value ?? 1));
    if (this._input) {
      this._input.value = String(qty);
    }
    this._scheduleUpdate(qty);
  };

  _handleRemove = () => {
    clearTimeout(this._debounceTimer);

    // Optimistic UI: animate the row out immediately
    const row = this.closest("tr.sb-bundle-row");
    if (row instanceof HTMLElement) {
      row.style.setProperty("--row-height", `${row.clientHeight}px`);
      row.classList.add("removing");
      row.addEventListener("animationend", () => row.remove(), { once: true });
    }

    this._removeAll();
  };

  /**
   * Removes all bundle line items sequentially via /cart/change.js,
   * then morphs the cart section and dispatches CartUpdateEvent.
   */
  async _removeAll() {
    const { sectionId, sections } = this._sectionInfo;
    /** @type {Record<string, unknown> | undefined} */
    let cart;

    for (const key of this._keys) {
      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: key,
          quantity: 0,
          sections,
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) {
        console.error(
          "sb-bundle-quantity: cart update failed",
          response.status
        );
        return;
      }

      cart = await response.json();
    }

    if (!cart) {
      return;
    }

    const cartSections = /** @type {Record<string, string>} */ (
      cart.sections ?? {}
    );
    if (sectionId && cartSections[sectionId]) {
      await morphSection(sectionId, cartSections[sectionId]);
    }

    this.dispatchEvent(
      new CartUpdateEvent(cart, this.dataset.bundleId ?? "", {
        sections: cartSections,
      })
    );
  }

  /**
   * Updates all bundle line items in parallel via /cart/change.js,
   * then morphs the cart section and dispatches CartUpdateEvent.
   * @param {number} qty
   */
  async _updateAll(qty) {
    const { sectionId, sections } = this._sectionInfo;

    const responses = await Promise.all(
      this._keys.map((key) =>
        fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: key,
            quantity: qty,
            sections,
            sections_url: window.location.pathname,
          }),
        })
      )
    );

    for (const response of responses) {
      if (!response.ok) {
        console.error(
          "sb-bundle-quantity: cart update failed",
          response.status
        );
        return;
      }
    }

    // All responses reflect the same final cart state; read from any one of them
    const lastResponse = responses.reduce((_, r) => r);
    const cart = /** @type {Record<string, unknown>} */ (
      await lastResponse.json()
    );
    const cartSections = /** @type {Record<string, string>} */ (
      cart.sections ?? {}
    );

    if (sectionId && cartSections[sectionId]) {
      await morphSection(sectionId, cartSections[sectionId]);
    }

    this.dispatchEvent(
      new CartUpdateEvent(cart, this.dataset.bundleId ?? "", {
        sections: cartSections,
      })
    );
  }
}

if (!customElements.get("sb-bundle-quantity")) {
  customElements.define("sb-bundle-quantity", SbBundleQuantity);
}
